import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { useProjectsList } from '../../../../../../contexts/ProjectsContext';
import {
  getSalesActivityTargets,
  saveSalesActivityTargets
} from '../../../../services/api/implementation/impl';
import ReportShell, { FilterSelect } from '../shared/ReportShell';
import useDailyReport from '../shared/useDailyReport';
import { errorMessage } from '../shared/format';

const FIELDS = [
  { key: 'followUps', api: 'follow_ups', label: 'Follow-ups / day' },
  { key: 'siteVisits', api: 'site_visits', label: 'Site visits / day' },
  { key: 'conversions', api: 'conversions', label: 'Conversions / day' }
];

const toInput = (v) => (v === null || v === undefined ? '' : String(v));

/**
 * Daily activity targets per salesperson. A change applies from the chosen
 * date onwards; earlier reports keep the target that was in force then.
 */
const ActivityTargets = () => {
  const { projectsForDropdown = [] } = useProjectsList();
  const [projectId, setProjectId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(moment().format('YYYY-MM-DD'));
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const params = useMemo(() => ({ project_id: projectId }), [projectId]);
  const { data, loading, error, refetch } = useDailyReport(getSalesActivityTargets, params);
  const rows = data?.targets || [];
  const canEdit = !!data?.canEdit;

  // Reset the editable grid whenever fresh targets arrive.
  useEffect(() => {
    const next = {};
    (data?.targets || []).forEach((r) => {
      const source = r.upcoming || r;
      next[r.salesUserId] = Object.fromEntries(FIELDS.map((f) => [f.key, toInput(source[f.key])]));
    });
    setDraft(next);
  }, [data]);

  const changed = rows.filter((r) => {
    const source = r.upcoming || r;
    const d = draft[r.salesUserId];
    return d && FIELDS.some((f) => d[f.key] !== toInput(source[f.key]));
  });

  const setValue = (userId, key, value) => {
    if (value !== '' && !/^\d{1,5}$/.test(value)) return;
    setDraft((prev) => ({ ...prev, [userId]: { ...prev[userId], [key]: value } }));
  };

  const save = async () => {
    if (!changed.length) return;
    setSaving(true);
    try {
      await saveSalesActivityTargets(
        changed.map((r) => ({
          sales_user_id: r.salesUserId,
          project_id: projectId ? Number(projectId) : null,
          effective_from: effectiveFrom,
          ...Object.fromEntries(FIELDS.map((f) => [f.api, Number(draft[r.salesUserId][f.key] || 0)]))
        }))
      );
      toast.success(`Saved targets for ${changed.length} ${changed.length === 1 ? 'person' : 'people'}.`);
      refetch();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save targets.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ReportShell
      title="Daily Activity Targets"
      subtitle="Per salesperson, per working day. Used for target and variance on the Sales Daily Report."
      loading={loading}
      hasData={!!data}
      error={error}
      onRetry={refetch}
      actions={
        <Link
          to="/builder/reports/sales-daily"
          className="flex items-center gap-2 min-h-[44px] px-4 border border-[#526477] rounded-md text-sm font-semibold text-[#111827] no-underline hover:bg-gray-50"
        >
          <ArrowLeft size={18} aria-hidden="true" />
          Back to report
        </Link>
      }
      filters={
        <>
          <FilterSelect label="Project" value={projectId} onChange={setProjectId} minWidth={220}>
            <option value="">All projects</option>
            {projectsForDropdown.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </FilterSelect>
          {canEdit && (
            <label className="flex items-center gap-2 text-sm font-semibold text-[#111827]">
              Applies from
              <input
                type="date"
                value={effectiveFrom}
                min={moment().subtract(1, 'day').format('YYYY-MM-DD')}
                onChange={(e) => setEffectiveFrom(e.target.value)}
                className="min-h-[44px] px-3 border border-[#526477] rounded-md bg-white"
              />
            </label>
          )}
        </>
      }
    >
      {!canEdit && (
        <p className="m-0 text-sm text-gray-700 bg-white rounded-xl p-4 shadow-sm">
          Only Management and Sales heads can change targets. These are the targets in force today.
        </p>
      )}
      <div className="bg-white rounded-2xl shadow-md overflow-x-auto">
        <table className="w-full min-w-[760px] border-collapse text-sm">
          <thead>
            <tr className="bg-slate-100 text-left text-gray-700">
              <th className="px-5 py-3.5 font-semibold">Salesperson</th>
              {FIELDS.map((f) => (
                <th key={f.key} className="px-4 py-3.5 font-semibold">
                  {f.label}
                </th>
              ))}
              <th className="px-5 py-3.5 font-semibold">In force since</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={FIELDS.length + 2} className="px-5 py-6 text-gray-600">
                  No salespeople found.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.salesUserId} className="border-t border-gray-200">
                <td className="px-5 py-3 font-semibold text-gray-900">{r.name}</td>
                {FIELDS.map((f) => (
                  <td key={f.key} className="px-4 py-3">
                    {canEdit ? (
                      <input
                        type="text"
                        inputMode="numeric"
                        aria-label={`${f.label} for ${r.name}`}
                        value={draft[r.salesUserId]?.[f.key] ?? ''}
                        placeholder="—"
                        onChange={(e) => setValue(r.salesUserId, f.key, e.target.value.trim())}
                        className="w-24 min-h-[40px] px-3 border border-gray-300 rounded-md"
                      />
                    ) : (
                      toInput(r[f.key]) || '—'
                    )}
                  </td>
                ))}
                <td className="px-5 py-3 text-gray-700">
                  {r.effectiveFrom ? moment(r.effectiveFrom).format('D MMM YYYY') : 'Not set'}
                  {r.upcoming && (
                    <span className="block text-xs text-amber-800">
                      Changes on {moment(r.upcoming.effectiveFrom).format('D MMM YYYY')}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={!changed.length || saving}
            className="min-h-[44px] px-5 bg-[#1F4B55] text-white rounded-md text-sm font-semibold hover:bg-[#163a42] disabled:opacity-50"
          >
            {saving ? 'Saving…' : `Save ${changed.length ? `(${changed.length})` : ''}`}
          </button>
          <span className="text-sm text-gray-600">
            A range of several days counts the daily target once per working day (Sundays and holidays excluded).
          </span>
        </div>
      )}
    </ReportShell>
  );
};

export default ActivityTargets;
